import { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutFetch } from "../../../store/authSlice";
import { getLoginPath } from "../../../utils/authRedirect";
import { getPortalByRole } from "../../../utils/portalTypes";
import { ToastProvider } from "../../ui";
import { NAV_ICONS, IconLogout, IconSettings } from "../../ui/icons";
import GlobalPatientSearch from "../GlobalPatientSearch";
import logo from "../../../assets/logo.png";
import L from "../../../constants/baysportLabels";
import { hasAdminAccess, staffNavRole, normalizeRole } from "../../../utils/roles";
import { buildAdminSidebarNav } from "../../../utils/adminNav";
import { resolvePlanAccess } from "../../../utils/planEntitlements";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import s from "../clinic.module.css";

const NAV = {
  client: [
    { to: "/app", end: true, label: "Главная", icon: "overview" },
    { to: "/app/book", label: "Запись", icon: "appointments" },
    { to: "/app/visits", label: "Занятия", icon: "schedule" },
    { to: "/app/profile", label: "Профиль", icon: "settings" },
  ],
  trainer: [
    { to: "/staff/app", end: true, label: "Сводка", icon: "overview" },
    { to: "/staff/app/schedule", label: "Расписание", icon: "schedule" },
    { to: "/staff/app/clients", label: "Клиенты", icon: "clients" },
    { to: "/staff/app/journal", label: "Журнал", icon: "journal" },
    { to: "/staff/app/life", label: "Клуб", icon: "life" },
    { to: "/staff/app/profile", label: "Профиль", icon: "settings" },
  ],
};

const ROLE_LABEL = {
  owner: "Владелец",
  admin: "Администратор",
  trainer: L.trainer,
  client: L.client,
};

function NavItems({ items, className = "" }) {
  return items.map((item) => {
    const Icon = NAV_ICONS[item.icon] || NAV_ICONS.overview;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        className={({ isActive }) =>
          isActive ? `${s.navLink} ${s.navLinkActive} ${className}` : `${s.navLink} ${className}`
        }
      >
        <Icon className={s.navIcon} />
        <span>{item.label}</span>
      </NavLink>
    );
  });
}

function ClientBottomNav({ items }) {
  return (
    <nav className={s.clientBottomNav} aria-label="Навигация">
      {items.map((item) => {
        const Icon = NAV_ICONS[item.icon] || NAV_ICONS.overview;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              isActive ? `${s.clientBottomNavItem} ${s.clientBottomNavItemActive}` : s.clientBottomNavItem
            }
          >
            <Icon className={s.clientBottomNavIcon} />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function ClinicShell({ children, title, subtitle, settingsQuickLink }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile(768);
  const { profile, tenant, role, club, billing, entitlements } = useSelector((state) => state.auth);
  const [navVersion, setNavVersion] = useState(0);

  useEffect(() => {
    const refresh = () => setNavVersion((v) => v + 1);
    window.addEventListener("baysport-nav-refresh", refresh);
    return () => window.removeEventListener("baysport-nav-refresh", refresh);
  }, []);

  const navRole = staffNavRole(role);
  const r = normalizeRole(role);
  const isStaff = hasAdminAccess(role) || r === "trainer";
  const isClient = r === "client";
  const showTopBar = Boolean(title || subtitle || hasAdminAccess(role));

  const adminNav =
    navRole === "admin"
      ? buildAdminSidebarNav(club, tenant, {
          billing,
          entitlements,
          role,
          _v: navVersion,
        })
      : null;
  const plan = resolvePlanAccess(entitlements, tenant, billing);
  const links =
    navRole === "admin"
      ? null
      : (NAV[navRole] || []).filter((item) => {
          if (String(item.to || "").includes("/life")) return plan.clubLife;
          return true;
        });

  const logout = () => {
    dispatch(logoutFetch());
    navigate(getLoginPath(role));
  };

  const initials = String(profile?.full_name || "?")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const portalHome = getPortalByRole(role).home;
  const clientMobile = isClient && isMobile;

  return (
    <div
      className={`${s.shell} ${isStaff ? s.shellStaff : ""} ${isClient ? s.shellPatient : ""} ${clientMobile ? s.shellPatientMobile : ""}`}
    >
      <aside className={`${s.sidebar} ${clientMobile ? s.sidebarClientMobile : ""}`}>
        <Link to={portalHome} className={s.sidebarBrand}>
          <img src={logo} alt="" className={s.sidebarLogo} />
          <div className={s.sidebarBrandText}>
            <span className={s.sidebarBrandName}>{tenant?.name || L.club}</span>
            {!clientMobile ? (
              <span className={s.sidebarBrandRole}>{ROLE_LABEL[r] || ROLE_LABEL[role]}</span>
            ) : null}
          </div>
        </Link>

        {!clientMobile ? (
          <nav className={s.nav}>
            {adminNav ? (
              <>
                <NavItems items={adminNav.main} />
                {adminNav.more?.length > 0 ? (
                  <>
                    <div className={s.navSectionLabel}>Ещё</div>
                    <NavItems items={adminNav.more} />
                  </>
                ) : null}
                {adminNav.extended?.length > 0 ? (
                  <>
                    <div className={s.navSectionLabel}>Расширение</div>
                    <NavItems items={adminNav.extended} />
                  </>
                ) : null}
                <div className={s.navSpacer} />
                <NavItems items={adminNav.footer} />
              </>
            ) : (
              <NavItems items={links} />
            )}
          </nav>
        ) : null}

        <div className={`${s.userBar} ${clientMobile ? s.userBarClientMobile : ""}`}>
          <div className={s.userAvatar} aria-hidden>
            {initials}
          </div>
          {!clientMobile ? (
            <div className={s.userInfo}>
              <div className={s.userName}>{profile?.full_name || "—"}</div>
              <div className={s.userRole}>{ROLE_LABEL[r] || ""}</div>
            </div>
          ) : (
            <div className={s.userInfo}>
              <div className={s.userName}>{profile?.full_name?.split(" ")[0] || "—"}</div>
            </div>
          )}
          <button
            type="button"
            className={s.logoutBtn}
            onClick={logout}
            title="Выйти"
            aria-label="Выйти"
          >
            <IconLogout className={s.navIcon} />
          </button>
        </div>
      </aside>

      <main className={`${s.main} ${clientMobile ? s.mainClientMobile : ""}`}>
        {showTopBar ? (
          <div className={`${s.mainTopBar} ${clientMobile ? s.mainTopBarClientMobile : ""}`}>
            {(title || subtitle || settingsQuickLink) && (
              <header className={s.pageHeader}>
                <div className={s.pageHeaderRow}>
                  {title ? <h1 className={s.pageTitle}>{title}</h1> : null}
                  {settingsQuickLink ? (
                    <Link
                      to="/manage/app/settings"
                      className={s.pageHeaderSettings}
                      title="Настройки"
                      aria-label="Настройки"
                    >
                      <IconSettings width={22} height={22} />
                    </Link>
                  ) : null}
                </div>
                {subtitle && !clientMobile ? (
                  <p className={s.pageSubtitle}>{subtitle}</p>
                ) : null}
              </header>
            )}
            {hasAdminAccess(role) ? <GlobalPatientSearch /> : null}
          </div>
        ) : null}

        {children}
      </main>

      {clientMobile ? <ClientBottomNav items={NAV.client} /> : null}
    </div>
  );
}

export default function ClinicLayout(props) {
  return (
    <ToastProvider>
      <ClinicShell {...props} />
    </ToastProvider>
  );
}
